"use client"

import * as React from "react"
import { Check, Copy } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

/**
 * Puts `value` on the clipboard and says so. The API is missing on an insecure
 * origin and can be refused by the browser, so the failure is reported rather
 * than swallowed — a silently empty clipboard costs somebody a wrong transfer.
 */
export function copyToClipboard(value: string, message?: string): Promise<boolean> {
  const clipboard = navigator.clipboard
  const done = clipboard ? clipboard.writeText(value) : Promise.reject(new Error("unsupported"))
  return done.then(
    () => {
      toast.info(message || "Copied to clipboard")
      return true
    },
    () => {
      toast.error("Could not copy to clipboard")
      return false
    }
  )
}

interface CopyButtonProps {
  value: string
  /** Toast wording; defaults to the generic "Copied to clipboard". */
  message?: string
  /** Accessible name — say what is being copied, not just "Copy". */
  ariaLabel?: string
  className?: string
}

/** Icon-only copy control, flipping to a tick for a moment after a copy. */
export function CopyButton({ value, message, ariaLabel, className }: CopyButtonProps) {
  const [copied, setCopied] = React.useState(false)

  React.useEffect(() => {
    if (!copied) return
    const timer = window.setTimeout(() => setCopied(false), 1500)
    return () => window.clearTimeout(timer)
  }, [copied])

  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={ariaLabel || "Copy"}
      className={cn("h-6 w-6 shrink-0", className)}
      onClick={() => {
        copyToClipboard(value, message).then(setCopied)
      }}
    >
      {copied ? (
        <Check className="h-4 w-4 text-success-500" />
      ) : (
        <Copy className="h-4 w-4 text-neutral-700" />
      )}
    </Button>
  )
}
